export type SrtBlock = {
    start: number;
    end: number;
    text: string;
};

export type TranscriptionWord = {
    start: number;
    end: number;
    word: string;
    confidence?: number;
};

export type TranscriptionBlock = {
    id: number;
    start: number;
    end: number;
    text: string;
    words: TranscriptionWord[];
};

export type ConfidenceBucket = "red" | "amber" | "none";

export function parseSrt(srt: string): SrtBlock[] {
    const out: SrtBlock[] = [];
    const chunks = srt.replace(/\r\n/g, "\n").split(/\n\s*\n/);
    for (const chunk of chunks) {
        const trimmed = chunk.replace(/^\ufeff/, "").trim();
        if (!trimmed) continue;
        const lines = trimmed.split("\n");
        let i = 0;
        if (/^\d+$/.test(lines[0].trim())) i = 1;
        const tcLine = lines[i];
        const match = tcLine?.match(
            /(\d{1,2}:\d{2}:\d{2}[,.]\d{1,3})\s*-->\s*(\d{1,2}:\d{2}:\d{2}[,.]\d{1,3})/
        );
        if (!match) continue;
        const start = srtTimeToSeconds(match[1]);
        const end = srtTimeToSeconds(match[2]);
        const text = lines.slice(i + 1).join("\n");
        out.push({ start, end, text });
    }
    return out;
}

export function serializeSrt(blocks: SrtBlock[]): string {
    return (
        blocks
            .map(
                (b, i) =>
                    `${i + 1}\n${secondsToSrtTime(b.start)} --> ${secondsToSrtTime(b.end)}\n${b.text}`
            )
            .join("\n\n") + "\n"
    );
}

function srtTimeToSeconds(t: string): number {
    const norm = t.replace(".", ",");
    const [hms, ms] = norm.split(",");
    const [h, m, s] = hms.split(":").map(Number);
    return h * 3600 + m * 60 + s + Number(ms ?? 0) / 1000;
}

function pad(n: number, w = 2) {
    return String(n).padStart(w, "0");
}

export function secondsToSrtTime(s: number): string {
    if (!isFinite(s) || s < 0) s = 0;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60);
    const ms = Math.round((s - Math.floor(s)) * 1000);
    return `${pad(h)}:${pad(m)}:${pad(sec)},${pad(ms, 3)}`;
}

export function formatTimestamp(s: number): string {
    if (!isFinite(s) || s < 0) s = 0;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60);
    if (h > 0) return `${h}:${pad(m)}:${pad(sec)}`;
    return `${m}:${pad(sec)}`;
}

// Absolute confidence ceilings. A word is only flagged if it's both in
// the low-percentile band AND below this threshold — filters out tracks
// where the model was genuinely confident about almost everything.
const RED_MAX_CONFIDENCE = 0.5;
const AMBER_MAX_CONFIDENCE = 0.75;

export function buildConfidenceBucketer(
    confidences: (number | undefined)[]
): (c: number | undefined) => ConfidenceBucket {
    const valid = confidences.filter(
        (c): c is number => typeof c === "number" && !isNaN(c)
    );
    if (valid.length < 20) return () => "none";
    const sorted = [...valid].sort((a, b) => a - b);
    const at = (p: number) =>
        sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))];
    const p12 = at(0.12);
    const p30 = at(0.3);
    return (c) => {
        if (typeof c !== "number" || isNaN(c)) return "none";
        if (c <= p12 && c < RED_MAX_CONFIDENCE) return "red";
        if (c <= p30 && c < AMBER_MAX_CONFIDENCE) return "amber";
        return "none";
    };
}

export type BuildDocInput = {
    srt: string;
    transcription: TranscriptionBlock[] | null;
};

export type BuildDocResult = {
    doc: any;
};

export function buildCaptionsDoc({
    srt,
    transcription,
}: BuildDocInput): BuildDocResult {
    const blocks = parseSrt(srt);
    const allConfidences: number[] = [];
    for (const b of transcription ?? []) {
        for (const w of b.words ?? []) {
            if (typeof w.confidence === "number")
                allConfidences.push(w.confidence);
        }
    }
    const bucketize = buildConfidenceBucketer(allConfidences);

    const content = blocks.map((block, i) => {
        const transBlock = transcription?.[i];
        const words = transBlock?.words ?? [];
        const blockText = block.text;

        const inlines: any[] = [];
        if (words.length > 0 && blockText.length > 0) {
            let cursor = 0;
            for (let wIdx = 0; wIdx < words.length; wIdx++) {
                const w = words[wIdx];
                const token = w.word;
                if (!token) continue;
                const idx = blockText.indexOf(token, cursor);
                if (idx === -1) continue;
                if (idx > cursor) {
                    inlines.push({
                        type: "text",
                        text: blockText.slice(cursor, idx),
                    });
                }
                const bucket = bucketize(w.confidence);
                inlines.push({
                    type: "text",
                    text: token,
                    marks: [
                        {
                            type: "word",
                            attrs: {
                                confidence:
                                    typeof w.confidence === "number"
                                        ? w.confidence
                                        : null,
                                bucket,
                                original: token,
                                blockIdx: i,
                                wordIdx: wIdx,
                            },
                        },
                    ],
                });
                cursor = idx + token.length;
            }
            if (cursor < blockText.length) {
                inlines.push({
                    type: "text",
                    text: blockText.slice(cursor),
                });
            }
        }

        if (inlines.length === 0 && blockText.length > 0) {
            inlines.push({ type: "text", text: blockText });
        }

        return {
            type: "captionBlock",
            attrs: { start: block.start, end: block.end, index: i },
            content: splitTextOnNewlines(inlines),
        };
    });

    return {
        doc: {
            type: "doc",
            content: content.length ? content : [emptyBlock()],
        },
    };
}

function emptyBlock() {
    return {
        type: "captionBlock",
        attrs: { start: 0, end: 0, index: 0 },
        content: [],
    };
}

function splitTextOnNewlines(inlines: any[]): any[] {
    const out: any[] = [];
    for (const node of inlines) {
        if (node.type !== "text" || !node.text?.includes("\n")) {
            if (node.type !== "text" || node.text?.length > 0) out.push(node);
            continue;
        }
        const parts = node.text.split("\n");
        parts.forEach((p: string, i: number) => {
            if (p.length > 0) out.push({ ...node, text: p });
            if (i < parts.length - 1) out.push({ type: "hardBreak" });
        });
    }
    return out;
}

export function serializeDocJsonToSrt(doc: any): string {
    const blocks: SrtBlock[] = [];
    for (const node of doc?.content ?? []) {
        if (node.type !== "captionBlock") continue;
        const text = inlineContentToText(node.content ?? []);
        blocks.push({
            start: Number(node.attrs?.start ?? 0),
            end: Number(node.attrs?.end ?? 0),
            text,
        });
    }
    return serializeSrt(blocks);
}

function inlineContentToText(content: any[]): string {
    let out = "";
    for (const n of content) {
        if (n.type === "text") out += n.text ?? "";
        else if (n.type === "hardBreak") out += "\n";
    }
    return out;
}
